import { Injectable, inject } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
	DataleanBaseApiService,
	DataleanPagedResult,
	FeatureValue,
	FeatureValueType,
	PaginationInfo,
	Parts,
	SimpleObject,
	SortInfo,
	Structure,
	StructureType,
} from 'addiction-components';
import { Observable, Subject, forkJoin } from 'rxjs';
import { HeaderLocaleService } from 'src/app/components';
import { BookableProductActions } from 'src/app/core/state/app.actions';
import { BookableProduct } from 'src/app/shared/models/reservations/bookable-product.interface';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class BookableProductService {
	private store = inject(Store);
	private translateService = inject(TranslateService);
	private headerSrv = inject(HeaderLocaleService);
	private baseApi = inject(DataleanBaseApiService);
	private destroyed$ = new Subject();
	/* private lastCommunitySelected$ = this.store.select(CommunitySelectSelectors.selectLastCommunitySelected).pipe(takeUntil(this.destroyed$)); */
	private bookableProductUrl = `${environment.reservationProductUrl}`;

	constructor() {}

	init(): void {
		/* 		this.lastCommunitySelected$.pipe(takeUntil(this.destroyed$)).subscribe((communityUUID) => {
			this.getStructureAndFeatures(communityUUID);
		}); */
		this.getStructureAndFeatures();
	}

	destroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	getStructureAndFeatures() {
		const structureAdditionalParams: {
			type?: StructureType;
		} = { type: StructureType.BOOKABLE_PRODUCT };

		const featureAdditionalParams: {
			featureValueCommunityUUID?: string;
			type?: FeatureValueType;
			locale?: string;
		} = { type: FeatureValueType.BOOKABLE_PRODUCT, locale: this.translateService.currentLang ?? this.translateService.defaultLang };

		// TODO ATTIVARE QUANDO GESTITE LE COMUNITA'
		// if (communityUUID !== undefined) {
		// 	featureAdditionalParams.featureValueCommunityUUID = communityUUID;
		// } else if (this.lastCommunitySelected !== undefined) {
		// 	featureAdditionalParams.featureValueCommunityUUID = this.lastCommunitySelected;
		// }

		this.baseApi
			.getMany<Structure>(environment.structureUrl, [Parts.STRUCTURE_FIELDS], { additionalParams: structureAdditionalParams })
			.subscribe((structures) => {
				this.store.dispatch(BookableProductActions.setStructures({ structures }));
			});
		this.baseApi
			.getMany<FeatureValue>(environment.featureValueUrl, [Parts.FEATURE_VALUES], {
				additionalParams: featureAdditionalParams,
			})
			.subscribe((features) => {
				this.store.dispatch(BookableProductActions.setFeatures({ features }));
			});
	}

	getProducts(options: {
		parts?: Parts[];
		locale?: string;
		pagination?: PaginationInfo;
		sort?: SortInfo;
		filters?: { uuids?: string[]; structureUUID?: string; query?: string };
	}) {
		const { filters, parts, locale, pagination, sort } = options;

		const params: SimpleObject = {
			locale: locale ?? this.headerSrv.getActiveLocale(),
			active: true,
		};

		// TODO: generalizzare questa logica in una funzione reutilizzabile
		if (filters?.structureUUID) params['structureUUID'] = filters.structureUUID;
		if (filters?.query) {
			params['q'] = filters.query;
			params['searchFields'] = ['name']; // parametrizzare i campi di ricerca??
		}

		if (filters?.uuids) params['uuid'] = filters.uuids.join(',');

		return this.baseApi.getManyPaged<BookableProduct>(this.bookableProductUrl, parts ?? [Parts.ALL], {
			pagination,
			sort,
			additionalParams: params,
		});
	}

	getStructures(parts: Parts[]) {
		if (!parts) parts = [Parts.EMPTY];

		return this.baseApi.getManyPaged<Structure>(environment.structureUrl, parts);
	}

	countProducts(filters: { structureUUID?: string }) {
		return this.baseApi.getOne<{ count: number }>(this.bookableProductUrl + 'count', '', [], {
			organizationUUID: environment.organizationUUID,
			...filters,
			active: true,
		});
	}

	fetchProducts(
		pages: number[],
		sort?: Sort,
		locale?: string,
		gridSearch?: string | null,
		filters?: { featureValue?: string[]; structureUUID?: string }
	): Observable<DataleanPagedResult<BookableProduct>[]> {
		const obs: Observable<DataleanPagedResult<BookableProduct>>[] = [];

		const params: {
			sortBy?: string;
			q?: string;
			searchFields?: string;
			locale?: string;
			active: boolean;
			structureUUID?: string;
			featureValueList?: string;
		} = {
			locale: locale ?? this.headerSrv.getActiveLocale(),
			active: true,
		};

		// TODO: generalizzare questa logica in una funzione reutilizzabile
		if (filters?.structureUUID) params['structureUUID'] = filters.structureUUID;

		if (filters?.featureValue?.length) params['featureValueList'] = filters.featureValue.join(',') + '#and';

		//SORT
		if (sort) params.sortBy = `${sort.active}#${sort.direction}`;
		if (gridSearch) {
			params.q = gridSearch;
			params.searchFields = 'name,description';
		}

		for (const page of pages) {
			const pagination: PaginationInfo = new PaginationInfo(environment.pageSize, page);

			obs.push(
				this.baseApi.getManyPaged<BookableProduct>(this.bookableProductUrl, [Parts.CREATED_BY, Parts.UPDATE_DATE, Parts.VALUES], {
					pagination,
					additionalParams: params,
				})
			);
		}
		return forkJoin(obs);
	}

	deleteProduct(uuid: string) {
		return this.baseApi.deleteOne(this.bookableProductUrl, uuid);
	}
}
